// Third party
import moment from 'moment';

// Constants and types
import { COMPARISON_ITEM_TYPES, RISK_METRICS_KEYS, TIME_PERIODS } from './constants';
import CommonInception from '@/types/common-inception';
import ComparisonItem from '@/types/comparison-item';
import Risk from '@/types/risk';
import RiskMetric from '@/types/risk-metric';
import ComparisonItemPerformance from '@/types/comparison-item-performance';

// utils
import { getComparisonFilteredItems } from './comparison';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItemType from '@/types/comparison-item-type';
import ComparisonItemDates from '@/types/comparison-item-dates';
import Timestamp from '@/types/timestamp';

export const getCommonInception = (
  itemsContainer: { items: Array<ComparisonItem> },
  itemsFilter: Array<string>,
  useLongHistory?: boolean,
): CommonInception | undefined => {
  if (!itemsContainer?.items?.length) {
    return undefined;
  }

  const startTimestamp = getMaxCommonInceptionDateWithFilters(itemsContainer.items, itemsFilter, useLongHistory);

  if (startTimestamp === 0) {
    return undefined;
  }

  return {
    startTimestamp,
  };
};

export const setCommonInceptionRiskMetrics = (performance: ComparisonItemPerformance, riskMetrics: Risk): ComparisonItemPerformance => {
  let updatedMetrics = {};

  // Here we only want to update the CI value, this avoid dicrepancies or changes with the previously calculated values
  for (const key in RISK_METRICS_KEYS) {
    if (Object.prototype.hasOwnProperty.call(RISK_METRICS_KEYS, key)) {
      const property = RISK_METRICS_KEYS[key as keyof typeof RISK_METRICS_KEYS];
      updatedMetrics = {
        ...performance,
        ...updatedMetrics,
        [property]: {
          ...performance[property],
          [TIME_PERIODS.CI]: riskMetrics[property].CI || null,
        } as RiskMetric,
      };
    }
  }

  return updatedMetrics;
};

export const getMaxCommonInceptionDateWithFilters = (items: Array<ComparisonItem>, itemsFilter: Array<string>, useLongHistory?: boolean) => {
  const filteredItems = filterItems(items, itemsFilter);
  return getMaxCommonInceptionDate(filteredItems, useLongHistory);
};

export const getMaxCommonInceptionDate = (
  items: Array<{ holdings: Array<ComparisonHolding>; dates?: ComparisonItemDates; type?: ComparisonItemType }>,
  useLongHistory?: boolean,
) => {
  let mostRecentTimestamp: number | undefined;
  for (const item of items) {
    const timestamp = getFirstCompleteRolling(item.holdings, item.dates?.inception, item.type, useLongHistory);
    // if timestamp is null or 0, it means one of the rolling have no valid rolling, returning 0
    if (!timestamp) {
      return 0;
    }

    if (timestamp != null && (mostRecentTimestamp == null || timestamp > mostRecentTimestamp)) {
      mostRecentTimestamp = timestamp;
    }
  }

  return mostRecentTimestamp ? moment(mostRecentTimestamp).utc().startOf('month').toDate().getTime() : 0;
};

export const filterItems = (items: Array<ComparisonItem>, itemsFilter: Array<string>) => {
  const filteredItems = getComparisonFilteredItems({ items }, itemsFilter);
  const noBenchmarks = filteredItems.filter(item => item.type !== COMPARISON_ITEM_TYPES.BENCHMARK);
  return noBenchmarks;
};

// Get first rolling that is complete (inception not null or inception before or rolling month complete)
export const getFirstCompleteRolling = (
  holdings: Array<ComparisonHolding>,
  inceptionDate?: Date,
  comparisonItemType?: ComparisonItemType,
  useLongHistory?: boolean,
) => {
  if (comparisonItemType === COMPARISON_ITEM_TYPES.PORTFOLIO) {
    return getFirstCompleteRollingForPortfolio(holdings, useLongHistory);
  }

  return getFirstCompleteRollingForInstrument(holdings, inceptionDate);
};

export const getFirstCompleteRollingForPortfolio = (holdings: Array<ComparisonHolding>, useLongHistory?: boolean): number | Timestamp | undefined => {
  if (useLongHistory) {
    // Ignoring holdings that have no inception date for now, because calculation does as well
    const firstRolling = holdings.filter(holding => holding.dates?.inception).map(holding => holding.rolling?.monthly[0] && holding.rolling?.monthly[0][0]);
    return firstRolling.sort().reverse()[0];
  }
  const compItemFormat = holdings.map(h => ({ holdings: [h] }));
  return getMaxCommonInceptionDate(compItemFormat, useLongHistory);
};

export const getFirstCompleteRollingForInstrument = (holdings: Array<ComparisonHolding>, inceptionDate?: Date) => {
  try {
    const inceptionDateFromHolding = holdings[0]?.dates?.inception || inceptionDate;

    if (holdings[0].rolling?.monthly[0] == null) {
      return null;
    }

    const firstRolling = holdings[0].rolling?.monthly[0][0];

    // TODO: Since stock always have inception date as null for now, it will take the first rolling.
    // This might cause an issue where the first rolling have incomplete data (i.e. IPO being mid month)
    // Fix this by adding inception date on stocks
    if (inceptionDateFromHolding == null) {
      return holdings[0].rolling?.monthly[0][0];
    }

    const firstRollingMomentDate = moment(firstRolling).utc();
    const inceptionMomentDate = moment(inceptionDateFromHolding).utc();

    // if inception is after beginning of first rolling month, then first month have incomplete data, start on the 2nd month
    if (inceptionMomentDate.isAfter(firstRollingMomentDate.startOf('month'))) {
      return holdings[0].rolling?.monthly[1][0];
    }

    return holdings[0].rolling?.monthly[0][0];
  } catch (err) {
    console.error(err);
    return null;
  }
};
